import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { Card, Button, Input } from '@shared/components/ui';
import { Lock, RotateCw, Server, User, Cloud, AlertTriangle, CheckCircle } from 'lucide-react';

interface EncryptionSettings {
  encryption_key_type: 'PLATFORM' | 'CUSTOMER' | 'KMS';
  customer_encryption_key?: string;
  kms_arn?: string;
  kms_region?: string;
  encryption_key_rotated_at?: string;
}

const AUTO_DISMISS_DELAY = 5000;

export default function StorageSettingsContent() {
  const { t } = useTranslation('errors');
  const { t: storageT } = useTranslation('storage');
  const [settings, setSettings] = useState<EncryptionSettings>({ encryption_key_type: 'PLATFORM' });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const hadKeyInitially = useRef(false);
  const hasStartedInitialFetch = useRef(false);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), AUTO_DISMISS_DELAY);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (hasStartedInitialFetch.current) return;
    hasStartedInitialFetch.current = true;
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoadError(false);
    setMessage(null);
    try {
      setLoading(true);
      const res = await api.get<EncryptionSettings>('/settings/encryption');
      setSettings(res.data);
      hadKeyInitially.current = !!res.data.customer_encryption_key;
    } catch (_err) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || loadError) return;
    setMessage(null);

    if (
      settings.encryption_key_type === 'CUSTOMER' &&
      showKeyInput &&
      !settings.customer_encryption_key
    ) {
      setMessage({ type: 'error', text: t('errors.provideKey') });
      return;
    }

    if (settings.encryption_key_type === 'KMS' && (!settings.kms_arn || !settings.kms_region)) {
      setMessage({ type: 'error', text: t('errors.provideKms') });
      return;
    }

    try {
      setSaving(true);
      const payload: any = {
        encryption_key_type: settings.encryption_key_type,
        kms_arn: settings.kms_arn || null,
        kms_region: settings.kms_region || null,
      };

      const shouldSendKey =
        settings.encryption_key_type === 'CUSTOMER' &&
        (showKeyInput || !hadKeyInitially.current) &&
        !!settings.customer_encryption_key;

      if (shouldSendKey) payload.customer_encryption_key = settings.customer_encryption_key;

      await api.put('/settings/encryption', payload);
      setMessage({ type: 'success', text: t('success.saved') });
      setShowKeyInput(false);
    } catch {
      setMessage({ type: 'error', text: t('errors.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const handleKeyTypeChange = (type: 'PLATFORM' | 'CUSTOMER' | 'KMS') => {
    setSettings({
      ...settings,
      encryption_key_type: type,
      customer_encryption_key: type === 'CUSTOMER' ? settings.customer_encryption_key : '',
      kms_arn: type === 'KMS' ? settings.kms_arn : '',
      kms_region: type === 'KMS' ? settings.kms_region : '',
    });
    if (type !== 'CUSTOMER') setShowKeyInput(false);
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <Card className="p-5 sm:p-6">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-text-primary-light dark:text-text-primary-dark">
            {storageT('settings.encryptionStrategy')}
          </h2>
          <p className="text-text-muted-light dark:text-text-muted-dark text-sm mt-1 font-medium">
            {storageT('settings.encryptionSubtitle')}
          </p>
        </div>
        <Lock className="text-brand-600 dark:text-brand-400 w-8 h-8 opacity-20" />
      </div>

      {message && (
        <div
          role={message.type === 'error' ? 'alert' : 'status'}
          aria-live="polite"
          className={`mb-6 p-4 rounded-xl border text-sm font-medium ${
            message.type === 'success'
              ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 text-state-ok-light dark:text-state-ok-dark'
              : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 text-state-danger-light dark:text-state-danger-dark'
          }`}
        >
          {message.text}
        </div>
      )}

      {loadError ? (
        <div
          role="alert"
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-state-danger-light/20 dark:border-state-danger-dark/20 bg-state-danger-bg-light dark:bg-state-danger-bg-dark p-4 text-sm font-medium text-state-danger-light dark:text-state-danger-dark"
        >
          <span>{t('errors.loadFailed')}</span>
          <Button variant="outline" onClick={fetchSettings}>
            {t('500.retry')}
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-6 sm:space-y-8">
          <div
            role="radiogroup"
            aria-label={storageT('settings.encryptionStrategy')}
            className="grid grid-cols-1 gap-4"
          >
            <KeyOption
              type="PLATFORM"
              title={storageT('settings.platformManaged.title')}
              description={storageT('settings.platformManaged.description')}
              icon={<Server />}
              selected={settings.encryption_key_type === 'PLATFORM'}
              onClick={() => handleKeyTypeChange('PLATFORM')}
            />
            <KeyOption
              type="CUSTOMER"
              title={storageT('settings.customerManaged.title')}
              description={storageT('settings.customerManaged.description')}
              icon={<User />}
              selected={settings.encryption_key_type === 'CUSTOMER'}
              onClick={() => handleKeyTypeChange('CUSTOMER')}
            />
            <KeyOption
              type="KMS"
              title={storageT('settings.awsKms.title')}
              description={storageT('settings.awsKms.description')}
              icon={<Cloud />}
              selected={settings.encryption_key_type === 'KMS'}
              onClick={() => handleKeyTypeChange('KMS')}
            />
          </div>

          {settings.encryption_key_type === 'CUSTOMER' && (
            <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark rounded-2xl border border-surface-border-light dark:border-surface-border-dark space-y-4 animate-in fade-in slide-in-from-top-2">
              {showKeyInput || !settings.customer_encryption_key ? (
                <Input
                  id="customer-encryption-key"
                  label={t('customer.keyLabel')}
                  type="password"
                  placeholder={t('customer.keyPlaceholder')}
                  value={settings.customer_encryption_key || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, customer_encryption_key: e.target.value })
                  }
                  className="font-mono"
                />
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-state-ok-light dark:text-state-ok-dark font-bold text-sm">
                    <CheckCircle className="w-5 h-5" /> {t('customer.keyConfigured')}
                  </div>
                  <Button
                    size="md"
                    variant="outline"
                    type="button"
                    onClick={() => {
                      setShowKeyInput(true);
                      setSettings({ ...settings, customer_encryption_key: '' });
                    }}
                  >
                    <RotateCw className="w-4 h-4 mr-2" /> {t('customer.rotateKey')}
                  </Button>
                </div>
              )}

              <div className="flex gap-3 p-3 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-xl text-xs text-state-warn-light dark:text-state-warn-dark  font-medium">
                <AlertTriangle className="shrink-0 w-5 h-5" />
                <p>{t('customer.warning')}</p>
              </div>
            </div>
          )}

          {settings.encryption_key_type === 'KMS' && (
            <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark rounded-2xl border border-surface-border-light dark:border-surface-border-dark space-y-4 animate-in fade-in slide-in-from-top-2">
              <Input
                id="kms-arn"
                label={t('kms.arnLabel')}
                placeholder={t('kms.arnPlaceholder')}
                value={settings.kms_arn || ''}
                onChange={(e) => setSettings({ ...settings, kms_arn: e.target.value })}
              />
              <div>
                <label
                  htmlFor="kms-region"
                  className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                >
                  {t('kms.regionLabel')}
                </label>
                <select
                  className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                  id="kms-region"
                  value={settings.kms_region || 'us-east-1'}
                  onChange={(e) => setSettings({ ...settings, kms_region: e.target.value })}
                >
                  <option value="us-east-1">us-east-1 (N. Virginia)</option>
                  <option value="us-west-2">us-west-2 (Oregon)</option>
                  <option value="eu-west-1">eu-west-1 (Ireland)</option>
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
            <Button type="submit" isLoading={saving}>
              {t('saveButton')}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function KeyOption({
  type,
  title,
  description,
  icon,
  selected,
  onClick,
}: {
  type: EncryptionSettings['encryption_key_type'];
  title: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <label
      className={`relative w-full cursor-pointer text-left p-4 rounded-xl border-2 transition-all flex items-start gap-4 focus-within:ring-2 focus-within:ring-brand-500 ${
        selected
          ? 'border-brand-500 bg-accent-soft-light dark:bg-accent-soft-dark'
          : 'border-surface-border-light dark:border-surface-border-dark hover:border-brand-200'
      }`}
    >
      <input
        type="radio"
        name="encryption-key-type"
        value={type}
        checked={selected}
        onChange={onClick}
        className="sr-only"
      />
      <span
        aria-hidden="true"
        className={`p-2 rounded-lg ${selected ? 'bg-brand-600 text-white' : 'bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark'}`}
      >
        {icon}
      </span>
      <span>
        <span
          className={`font-semibold ${selected ? 'text-accent-text-light dark:text-accent-text-dark' : 'text-text-primary-light dark:text-text-primary-dark'}`}
        >
          {title}
        </span>
        <span className="block text-sm text-text-muted-light dark:text-text-muted-dark mt-1">
          {description}
        </span>
      </span>
    </label>
  );
}
